import { useCallback, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { PrecisionType, TradeData } from "@shared/types";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context.tsx";
import useGraphDataSocket from "@shared/services/socket/useGraphDataSocket";
import { DISPLAY_CURRENCIES } from "common/constants";
import {
  getHedgeLongShare,
  getTradeRowLadder,
} from "common/NewLook/priceLadder.ts";

const FALLBACK_SIGNIFICANT_DIGITS = 6;

export const useTradeDayPriceChange = (tradeData: TradeData) => {
  const { change } = useGraphDataSocket(
    tradeData.symbol,
    tradeData.exchange,
    DISPLAY_CURRENCIES.USD
  );
  const dayChange = change ?? tradeData.price_graph_data?.change;
  return dayChange === undefined ? undefined : dayChange * 100;
};

/** Formats ladder prices with the symbol's price precision ("110,000"). */
export const useLadderPriceFormat = (symbol?: string, exchange?: string) => {
  const { precision } = usePrecisions({
    symbol,
    exchange,
    precisionType: PrecisionType.PRICE,
  });
  return useCallback(
    (price: number) =>
      precision === undefined || isNaN(precision)
        ? getLocaleStringWithPrecision(
            Number(price.toPrecision(FALLBACK_SIGNIFICANT_DIGITS))
          )
        : getLocaleStringWithPrecision(price, precision),
    [precision]
  );
};

/**
 * A trades-list row's ladder model. Uses the live row from the PnL socket
 * when there is one, and the page's trades list to find a hedging leg's
 * other side.
 */
export const useTradeRowLadder = (
  tradeData: TradeData,
  liveData: Partial<TradeData> | undefined,
  isOpen: boolean
) => {
  const { getTradesQuery } = useContext(TradesPageContext) || {};
  const trades = getTradesQuery?.data?.data;
  const hedgeLongShare = getHedgeLongShare(tradeData, trades);
  return useMemo(
    () =>
      getTradeRowLadder(
        liveData ? { ...tradeData, ...liveData } : tradeData,
        isOpen,
        hedgeLongShare
      ),
    [tradeData, liveData, isOpen, hedgeLongShare]
  );
};

interface CachedTradesQuery {
  endpointName?: string;
  data?: { data?: TradeData[] };
}

/**
 * The trade drawer only loads one hedging leg; when an open-trades list is
 * already cached (trades page, dashboard), the other leg comes from it.
 */
export const useCachedHedgeLongShare = (tradeId?: number, enabled = true) =>
  useSelector((state: { sharedApi?: { queries?: object } }) => {
    if (!enabled || !tradeId) return undefined;
    const queries = Object.values(state.sharedApi?.queries || {}) as (
      | CachedTradesQuery
      | undefined
    )[];
    for (const query of queries) {
      if (query?.endpointName !== "getOpenTradesData") continue;
      const trades = query.data?.data;
      const trade = trades?.find((item) => item.id === tradeId);
      if (trade) return getHedgeLongShare(trade, trades);
    }
    return undefined;
  });

const TIME_UNITS: Record<string, string> = {
  Y: "y",
  M: "mo",
  W: "w",
  D: "d",
  h: "h",
  m: "m",
};

/**
 * The time in trade in one compact lowercase format ("45m", "9h", "2d",
 * "2w", "3mo") from the backend's "45m" / "9h" / "2D" / "2W" / "3M" / "1Y".
 */
export const formatTimeInTrade = (timePassed?: string) => {
  if (!timePassed) return timePassed;
  const match = timePassed.trim().match(/^(<?\d+)\s*([YMWDhm])$/);
  return match ? `${match[1]}${TIME_UNITS[match[2]]}` : timePassed;
};
